import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { BrowserRouter, Route } from 'react-router-dom';
import configureStore from './store/';
import '@salesforce-ux/design-system/assets/styles/salesforce-lightning-design-system.css';

import AppInit from './i18n/containers/AppInit';
import * as Common from './common/containers';
import * as Containers from './ecs/containers';
import * as ExperimentContainers from './experiment/containers';
import { default as FlagsProvider } from './flags/Flags';

import '@mc-prime/einstein-ui-components/dist/einstein-ui-components.css';
import { IconSettings, Settings } from '@salesforce/design-system-react';
import actionSprite from '@salesforce-ux/design-system/assets/icons/action-sprite/svg/symbols.svg';
import customSprite from '@salesforce-ux/design-system/assets/icons/custom-sprite/svg/symbols.svg';
import utilitySprite from '@salesforce-ux/design-system/assets/icons/utility-sprite/svg/symbols.svg';
import standardSprite from '@salesforce-ux/design-system/assets/icons/standard-sprite/svg/symbols.svg';
import doctypeSprite from '@salesforce-ux/design-system/assets/icons/doctype-sprite/svg/symbols.svg';

require('dotenv').config();

const store = configureStore();

const contentBlockApp = (
  <AppInit useBlockSdk={true}>
    <BrowserRouter>
      <IconSettings
        standardSprite={standardSprite}
        utilitySprite={utilitySprite}
        actionSprite={actionSprite}
        doctypeSprite={doctypeSprite}
        customSprite={customSprite}
      >
        <Route path="/" component={Common.ApiErrorToastContainer} />
        <Route
          exact
          path="/block/init"
          component={Containers.ContentBlockInit}
        />
        <Route
          exact
          path="/block/init/:appId"
          component={Containers.ContentBlockInit}
        />
        <Route
          exact
          path="/block/init/:appId/:stackKey"
          component={Containers.ContentBlockInit}
        />
        <Route exact path="/block" component={Containers.ContentBlock} />
        <Route
          exact
          path="/blocks/:blockId"
          component={Containers.ContentBlock}
        />
        <Route exact path="/error" component={Common.ErrorPage} />
      </IconSettings>
    </BrowserRouter>
  </AppInit>
);

const experimentBlockApp = (
  <AppInit useBlockSdk={true}>
    <BrowserRouter>
      <IconSettings
        standardSprite={standardSprite}
        utilitySprite={utilitySprite}
        actionSprite={actionSprite}
        doctypeSprite={doctypeSprite}
        customSprite={customSprite}
      >
        <Route path="/" component={Common.ApiErrorToastContainer} />
        <Route
          exact
          path="/experiments/block/init"
          component={ExperimentContainers.ExperimentBlockInit}
        />
        <Route
          exact
          path="/experiments/block/init/:appId"
          component={ExperimentContainers.ExperimentBlockInit}
        />
        <Route
          exact
          path="/experiments/block/init/:appId/:stackKey"
          component={ExperimentContainers.ExperimentBlockInit}
        />
        <Route
          exact
          path="/experiments/block"
          component={ExperimentContainers.ExperimentBlock}
        />
        <Route exact path="/error" component={Common.ErrorPage} />
      </IconSettings>
    </BrowserRouter>
  </AppInit>
);

const inactivePage = (
  <AppInit useBlockSdk={false}>
    <BrowserRouter>
      <IconSettings
        standardSprite={standardSprite}
        utilitySprite={utilitySprite}
        actionSprite={actionSprite}
        doctypeSprite={doctypeSprite}
        customSprite={customSprite}
      >
        <Route exact path="/inactive" component={Containers.InactivePage} />
      </IconSettings>
    </BrowserRouter>
  </AppInit>
);

const marketingCloudApp = (
  <AppInit useBlockSdk={false}>
    <FlagsProvider>
      <BrowserRouter>
        <IconSettings
          standardSprite={standardSprite}
          utilitySprite={utilitySprite}
          actionSprite={actionSprite}
          doctypeSprite={doctypeSprite}
          customSprite={customSprite}
        >
          <Route path="/" component={Common.ApiErrorToastContainer} />
          <Route exact path="/welcome" component={Containers.Welcome} />
          <Route exact path="/root" component={Containers.DistributionRoot} />
          <Route
            exact
            path="/root/analytics"
            component={Containers.AnalyticsRoot}
          />
          <Route
            exact
            path="/root/distribution"
            component={Containers.DistributionRoot}
          />
          <Route
            exact
            path="/root/settings"
            component={Containers.SettingsRoot}
          />
          <Route exact path="/" component={Containers.Home} />
          <Route exact path="/assets" component={Containers.AssetDashboard} />
          <Route
            exact
            path="/assets/:tab"
            component={Containers.AssetDashboard}
          />
          <Route exact path="/catalogs/:tab" component={Containers.Catalogs} />
          <Route exact path="/catalogs" component={Containers.Catalogs} />
          <Route
            exact
            path="/classes"
            component={Containers.AssetClassDashboard}
          />
          <Route exact path="/fallback" component={Containers.Fallback} />
          <Route exact path="/inactive" component={Containers.InactivePage} />
          <Route exact path="/rules" component={Containers.RuleSetup} />
          <Route exact path="/setup" component={Containers.Setup} />
          <Route exact path="/error" component={Common.ErrorPage} />
          <Route
            exact
            path="/attributes"
            component={Containers.AssetAttributeDashboard}
          />
          <Route
            exact
            path="/attributes/asset"
            component={Containers.AssetAttributeDashboard}
          />
          <Route
            exact
            path="/attributes/profile"
            component={Containers.ProfileAttributeDashboard}
          />
          <Route
            exact
            path="/analytics/reports"
            component={Containers.Reports}
          />
          <Route exact path="/analytics" component={Containers.AnalyticsRoot} />
          <Route exact path="/settings" component={Containers.SettingsRoot} />
          <Route
            exact
            path="/distribution"
            component={Containers.DistributionRoot}
          />
          <Route
            exact
            path="/profiles"
            component={Containers.ProfileDashboard}
          />
          <Route exact path="/profile" component={Containers.ProfileBrowser} />
          <Route
            exact
            path="/selections"
            component={Containers.SelectionDashboard}
          />
          <Route
            exact
            path="/selections/:subscriber/:jobId/:blockId"
            component={Containers.SelectionDetailsDashboard}
          />
          {/* McMenu Tabs */}
          <Route
            exact
            path="/experiments"
            component={ExperimentContainers.ExperimentDashboard}
          />
          <Route
            exact
            path="/experiments/analytics/:experimentId"
            component={ExperimentContainers.ExperimentAnalytics}
          />
        </IconSettings>
      </BrowserRouter>
    </FlagsProvider>
  </AppInit>
);

const routes = () => {
  if (window.location.pathname.startsWith('/inactive')) {
    return inactivePage;
  } else if (window.location.pathname.startsWith('/block')) {
    return contentBlockApp;
  } else if (window.location.pathname.startsWith('/experiments/block')) {
    return experimentBlockApp;
  } else {
    return marketingCloudApp;
  }
};

const router = (
  <Provider store={store} appElement={document.getElementById('root')}>
    {routes()}
  </Provider>
);

// Disable screenreaders for entire App when a Modal is open
Settings.setAppElement(document.getElementById('root'));

ReactDOM.render(router, document.getElementById('root'));
